import type { RequiredGeneric } from '@pba/utils';
import { type ProgramEventInviteeRole, ProgramInviteType } from 'models';
import { useMemo } from 'react';
import { type FormInstance, message } from 'ui';
import type {
  EmailSelectedUser,
  EmailSelectedUserWithRole,
  RegisteredSelectedUser,
  RegisteredSelectedUserWithRole,
} from '../../../../../types';
import type { CommunityOption } from '../../EventCommunity';

export type CommonFormValues = {
  emailInvitees?: EmailSelectedUserWithRole[];
  invitees?: RegisteredSelectedUserWithRole[];
};

export type RequiredFormValues = {
  community?: CommunityOption;
} & CommonFormValues;

type HookProperties = {
  form: FormInstance<RequiredFormValues>;
  onInviteesTypeChanged: (type: ProgramInviteType) => void;
  skipPostInCommunityValidation?: boolean;
};

export const mapInviteTypeToRole = (type: ProgramInviteType): ProgramEventInviteeRole => {
  const mapType = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Empty value
    '': 'attendee',
    invitees: 'attendee',
    moderators: 'moderator',
    presenters: 'presenter',
  } as const;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  return mapType[type] ?? mapType.invitees;
};

export const useEventInvitees = <T extends RequiredFormValues>({
  form,
  onInviteesTypeChanged,
  skipPostInCommunityValidation = false,
}: RequiredGeneric<T, HookProperties>) =>
  useMemo(() => {
    const setInvitees = (
      type: ProgramInviteType,
      invitees: RegisteredSelectedUser[],
      emailInvitees: EmailSelectedUser[],
    ) => {
      if (type === ProgramInviteType.empty) {
        throw new Error('Can not set invitees for empty type');
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      const currentInvitees: RegisteredSelectedUserWithRole[] = form.getFieldValue('invitees') ?? [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      const currentEmailInvitees: EmailSelectedUserWithRole[] = form.getFieldValue('emailInvitees') ?? [];

      const addedInvitees: RegisteredSelectedUserWithRole[] = invitees.map((invitee) => ({
        ...invitee,
        eventRole: mapInviteTypeToRole(type),
      }));

      const addedEmailInvitees: EmailSelectedUserWithRole[] = emailInvitees.map((invitee) => ({
        email: invitee.email,
        eventRole: mapInviteTypeToRole(type),
      }));

      form.setFieldsValue({
        emailInvitees: [
          ...currentEmailInvitees.filter(
            (invitee) => !addedEmailInvitees.some((newInvitee) => invitee.email === newInvitee.email),
          ),
          ...addedEmailInvitees,
        ],
        invitees: [
          ...currentInvitees.filter((invitee) => !addedInvitees.some((newInvitee) => invitee.id === newInvitee.id)),
          ...addedInvitees,
        ],
      });
    };

    const resetSelectedUsers = () => {
      setInvitees(ProgramInviteType.invitees, [], []);
      setInvitees(ProgramInviteType.moderators, [], []);
      setInvitees(ProgramInviteType.presenters, [], []);
    };

    const onInvite = (
      type: ProgramInviteType,
      invitees: RegisteredSelectedUser[],
      emailInvitees?: EmailSelectedUser[],
    ) => {
      setInvitees(type, invitees, emailInvitees ?? []);
      setInvitees(type, invitees, emailInvitees ?? []);
      setInvitees(type, invitees, emailInvitees ?? []);

      onInviteesTypeChanged(ProgramInviteType.empty);
    };

    const onShowInvitees = (type: ProgramInviteType) => () => {
      if (skipPostInCommunityValidation) {
        onInviteesTypeChanged(type);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        const { community } = form.getFieldsValue(true) as Partial<RequiredFormValues>;

        if (community) {
          onInviteesTypeChanged(type);
        } else {
          message.info('Please select community');
        }
      }
    };

    return {
      onInvite,
      onShowInvitees,
      resetSelectedUsers,
    };
  }, [skipPostInCommunityValidation, form, onInviteesTypeChanged]);
